import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Sparkles, ShieldCheck, Mail, Lock, User, AlertCircle, ArrowRight, Zap, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";

export const AuthModal = ({ isOpen, onClose }) => {
  const { login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password, name);
      } else {
        await login(email, password);
      }
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || "Ocurrió un error. Intenta de nuevo.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
      <div 
        data-testid="auth-modal"
        className="w-full max-w-md bg-[#12141D] border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl relative overflow-hidden"
      >
        {/* Glow effect */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-red-600/20 rounded-full blur-3xl pointer-events-none" />

        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-red-600/10 border border-red-500/30 text-red-500 mb-3">
            <Zap className="w-6 h-6 animate-pulse" />
          </div>
          <h2 className="text-2xl font-black tracking-tight text-white">
            {isRegister ? "Crear Cuenta Creador" : "Iniciar Sesión en CortoViral"}
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            {isRegister 
              ? "Gratis para siempre, con 48 h de prueba Pro al empezar" 
              : "Accede a tu panel y fábrica automatizada de videos"}
          </p>
        </div>

        {error && (
          <div 
            data-testid="auth-error-message"
            className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs sm:text-sm flex items-start gap-2"
          >
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {isRegister && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
                Tu Nombre / Canal
              </label>
              <div className="relative">
                <User className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
                <input
                  data-testid="auth-input-name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Canal Viral"
                  className="w-full bg-[#1A1D2B] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
              Correo Electrónico
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
              <input
                data-testid="auth-input-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@canal.com"
                className="w-full bg-[#1A1D2B] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
              Contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
              <input
                data-testid="auth-input-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[#1A1D2B] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>

          {isRegister && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-[11px] text-amber-300">
              <span className="font-semibold">Regla de Red:</span> Solo se permite 1 cuenta por conexión de red IP para proteger la cuota diaria de IA.
            </div>
          )}

          {isRegister && (
            <p className="text-[11px] text-slate-400">
              Al crear la cuenta aceptas las{" "}
              <a href="/terminos#terminos" target="_blank" rel="noopener noreferrer" className="text-red-400 underline">condiciones de uso</a>{" "}
              y la{" "}
              <a href="/privacidad#privacidad" target="_blank" rel="noopener noreferrer" className="text-red-400 underline">política de privacidad</a>.
            </p>
          )}

          <Button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-2.5 rounded-lg shadow-lg shadow-red-900/30 flex items-center justify-center gap-2 mt-2"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : isRegister ? (
              <>Crear cuenta gratis <ArrowRight className="w-4 h-4" /></>
            ) : (
              <>Entrar a CortoViral <ArrowRight className="w-4 h-4" /></>
            )}
          </Button>
        </form>

        <div className="mt-5 text-center text-xs text-slate-400">
          {isRegister ? (
            <p>
              ¿Ya tienes cuenta?{" "}
              <button
                data-testid="switch-to-login-btn"
                type="button"
                onClick={() => { setIsRegister(false); setError(""); }}
                className="text-red-400 hover:text-red-300 font-semibold underline underline-offset-2 ml-1"
              >
                Inicia sesión aquí
              </button>
            </p>
          ) : (
            <p>
              ¿No tienes cuenta?{" "}
              <button
                data-testid="switch-to-register-btn"
                type="button"
                onClick={() => { setIsRegister(true); setError(""); }}
                className="text-red-400 hover:text-red-300 font-semibold underline underline-offset-2 ml-1"
              >
                Regístrate gratis
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
